import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { Property } from '../types/property.types';

interface MapComponentProps {
  properties: Property[];
  selectedProperty?: Property | null;
  onPropertySelect?: (property: Property) => void;
}

const MapComponent: React.FC<MapComponentProps> = ({ 
  properties, 
  selectedProperty, 
  onPropertySelect 
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingMarkers, setLoadingMarkers] = useState(false);
  const [visibleProperties, setVisibleProperties] = useState<Property[]>([]);
  const [retryCount, setRetryCount] = useState(0);
  const [isElementReady, setIsElementReady] = useState(false);

  // Coordenadas por defecto para Medellín
  const DEFAULT_CENTER = { lat: 6.2442, lng: -75.5812 };

  // Limitar propiedades mostradas para mejor rendimiento
  const MAX_MARKERS = 50;
  const MAX_RETRIES = 3;
  const INIT_TIMEOUT = 15000;
  
  // Propiedades prioritarias (optimizadas para mostrar primero)
  const prioritizedProperties = useMemo(() => {
    const available = properties.filter(p => p.estado === 'Disponible');
    const others = properties.filter(p => p.estado !== 'Disponible');
    
    // Mostrar primero las disponibles, luego las demás
    const prioritized = [...available, ...others].slice(0, MAX_MARKERS);
    
    console.log(`🎯 Mostrando ${prioritized.length} de ${properties.length} propiedades en el mapa`);
    return prioritized;
  }, [properties]);

  // Función para obtener coordenadas aproximadas basadas en el sector
  const getCoordinatesForSector = (property: Property) => {
    const sectorCoordinates: { [key: string]: { lat: number; lng: number } } = {
      'El Poblado': { lat: 6.2084, lng: -75.5684 },
      'Envigado': { lat: 6.1698, lng: -75.5939 },
      'Sabaneta': { lat: 6.1512, lng: -75.6164 },
      'Itagüí': { lat: 6.1734, lng: -75.6063 },
      'Laureles': { lat: 6.2484, lng: -75.5901 },
      'Estadio': { lat: 6.2574, lng: -75.5659 },
      'La América': { lat: 6.2484, lng: -75.6051 },
      'Belén': { lat: 6.2284, lng: -75.6151 }
    };

    const baseCoords = sectorCoordinates[property.sector] || DEFAULT_CENTER;
    
    // Agregar una pequeña variación aleatoria para evitar superposición
    return {
      lat: baseCoords.lat + (Math.random() - 0.5) * 0.01,
      lng: baseCoords.lng + (Math.random() - 0.5) * 0.01
    };
  };

  // Obtener color del marcador según el tipo de propiedad
  const getMarkerColor = (property: Property) => {
    const colors: { [key: string]: string } = {
      'Apto': '#3b82f6',     // Azul
      'Casa': '#10b981',     // Verde
      'Local': '#f59e0b',    // Naranja
      'Oficina': '#ef4444',  // Rojo
      'Consultorio': '#8b5cf6', // Púrpura
      'Bodega': '#6b7280'    // Gris
    };
    return colors[property.tipo_inmueble || 'default'] || '#6b7280';
  };

  // Función para formatear precios
  const formatPrice = (property: Property) => {
    const formatter = new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    if (property.transaccion === 'Ambos' && property.valor_venta && property.valor_arriendo) {
      return `Venta: ${formatter.format(property.valor_venta)} / Arriendo: ${formatter.format(property.valor_arriendo)}`;
    } else if (property.valor_venta) {
      return `Venta: ${formatter.format(property.valor_venta)}`;
    } else if (property.valor_arriendo) {
      return `Arriendo: ${formatter.format(property.valor_arriendo)}`;
    }
    return 'Precio no especificado';
  };

  // Verificar que el elemento DOM esté listo
  useEffect(() => {
    const checkElementReady = () => {
      if (mapRef.current && mapRef.current.offsetWidth > 0 && mapRef.current.offsetHeight > 0) {
        setIsElementReady(true);
        console.log('✅ Elemento del mapa está listo');
      } else {
        console.log('⏳ Esperando que el elemento del mapa esté listo...');
        setTimeout(checkElementReady, 100);
      }
    };

    checkElementReady();
  }, []);

  // Función para reintentar la carga
  const retryMapLoad = () => {
    if (retryCount < MAX_RETRIES) {
      console.log(`🔄 Reintentando carga del mapa (${retryCount + 1}/${MAX_RETRIES})`);
      setRetryCount(prev => prev + 1);
      setError(null);
      setLoading(true);
    } else {
      setError('No se pudo cargar el mapa después de varios intentos. Por favor, recarga la página.');
    }
  };

  // Inicializar el mapa con timeout y mejor manejo de errores
  useEffect(() => {
    if (!isElementReady) {
      console.log('⏳ Esperando que el elemento esté listo...');
      return;
    }

    const initMap = async () => {
      try {
        setLoading(true);
        setError(null);

        // Verificación más robusta del elemento
        if (!mapRef.current) {
          throw new Error('Referencia del mapa no disponible');
        }

        // Verificar que el elemento tenga dimensiones
        const rect = mapRef.current.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) {
          throw new Error('El elemento del mapa no tiene dimensiones válidas');
        }

        console.log('🗺️ Iniciando carga de Google Maps API...', {
          elementWidth: rect.width,
          elementHeight: rect.height
        });

        // Crear promise con timeout mejorado
        const mapLoadPromise = new Promise<google.maps.Map>(async (resolve, reject) => {
          try {
            // Verificar si ya existe una instancia de Google Maps cargada
            if (typeof window !== 'undefined' && (window as any).google?.maps) {
              console.log('🗺️ Google Maps ya está disponible globalmente');
              
              // Esperar un poco más antes de crear el mapa
              await new Promise(resolve => setTimeout(resolve, 500));
              
              if (!mapRef.current) {
                throw new Error('Elemento perdido durante la inicialización');
              }

              const mapInstance = new (window as any).google.maps.Map(mapRef.current, {
                center: DEFAULT_CENTER,
                zoom: 12,
                styles: [
                  {
                    featureType: 'poi',
                    elementType: 'labels',
                    stylers: [{ visibility: 'off' }]
                  }
                ],
              });
              
              // Esperar a que el mapa se renderice
              await new Promise(resolve => {
                google.maps.event.addListenerOnce(mapInstance, 'idle', resolve);
              });

              resolve(mapInstance);
              return;
            }

            // Cargar Google Maps API usando el Loader
            const loader = new Loader({
              apiKey: 'AIzaSyB0XG3mnbHALFLjZ4UZjjjOSeJnD0uFpdc',
              version: 'weekly',
              libraries: ['places'],
              language: 'es',
              region: 'CO'
            });

            console.log('📦 Cargando Google Maps API...');
            
            // Cargar la API
            const googleMaps = await loader.load();
            console.log('✅ Google Maps API cargada exitosamente');
            
            // Verificar nuevamente que el elemento esté disponible
            if (!mapRef.current) {
              throw new Error('Elemento del mapa perdido después de cargar la API');
            }

            // Pequeña pausa antes de crear el mapa
            await new Promise(resolve => setTimeout(resolve, 300));

            // Crear instancia del mapa
            const mapInstance = new googleMaps.maps.Map(mapRef.current, {
              center: DEFAULT_CENTER,
              zoom: 12,
              styles: [
                {
                  featureType: 'poi',
                  elementType: 'labels',
                  stylers: [{ visibility: 'off' }]
                }
              ],
            });

            // Esperar a que el mapa esté completamente cargado
            await new Promise(resolve => {
              googleMaps.maps.event.addListenerOnce(mapInstance, 'idle', resolve);
            });

            resolve(mapInstance);
          } catch (error) {
            reject(error);
          }
        });

        // Agregar timeout
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error('Timeout: La carga del mapa tomó demasiado tiempo'));
          }, INIT_TIMEOUT);
        });

        // Esperar el mapa o el timeout
        const mapInstance = await Promise.race([mapLoadPromise, timeoutPromise]);
        
        setMap(mapInstance);
        setLoading(false);
        setRetryCount(0); // Reset retry count on success
        console.log('🎉 Mapa inicializado correctamente');

      } catch (err) {
        console.error('❌ Error detallado al cargar Google Maps:', err);
        
        let errorMessage = 'Error al cargar el mapa';
        
        if (err instanceof Error) {
          if (err.message.includes('Timeout')) {
            errorMessage = 'El mapa tardó demasiado en cargar. Verifica tu conexión a internet.';
          } else if (err.message.includes('API key') || err.message.includes('ApiNotActivated')) {
            errorMessage = 'Error con la API Key de Google Maps. Verifica la configuración.';
          } else if (err.message.includes('quota') || err.message.includes('OverQuotaError')) {
            errorMessage = 'Límite de cuota excedido en Google Maps API.';
          } else if (err.message.includes('RefererNotAllowedMapError')) {
            errorMessage = 'Dominio no autorizado. Agrega localhost:3000 a las URLs permitidas.';
          } else if (err.message.includes('dimensiones')) {
            errorMessage = 'Error con las dimensiones del contenedor del mapa.';
          } else {
            errorMessage = `Error de Google Maps: ${err.message}`;
          }
        }
        
        setError(errorMessage);
        setLoading(false);
      }
    };

    // Delay inicial más largo antes de cargar
    const timeoutId = setTimeout(() => {
      initMap();
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isElementReady, retryCount]);

  // Crear ícono personalizado más simple y confiable
  const createCustomIcon = (property: Property) => {
    const color = getMarkerColor(property);
    
    // Usar un círculo simple con número o letra en lugar de emoji
    const getSymbol = (tipo: string) => {
      const symbols: { [key: string]: string } = {
        'Apto': 'A',
        'Casa': 'C', 
        'Local': 'L',
        'Oficina': 'O',
        'Consultorio': 'M',
        'Bodega': 'B'
      };
      return symbols[tipo] || 'P';
    };

    // Crear SVG más simple y confiable
    const svgIcon = `
      <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
        <circle cx="16" cy="16" r="14" fill="${color}" stroke="#ffffff" stroke-width="3"/>
        <text x="16" y="20" text-anchor="middle" font-size="14" font-weight="bold" fill="#ffffff">${getSymbol(property.tipo_inmueble || 'default')}</text>
      </svg>
    `;

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgIcon)}`,
      scaledSize: new google.maps.Size(32, 32),
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(16, 16)
    };
  };

  // Agregar marcadores al mapa usando marcadores básicos
  useEffect(() => {
    if (!map || !prioritizedProperties.length) return;

    const addMarkersProgressively = async () => {
      setLoadingMarkers(true);
      console.log(`📍 Iniciando carga progresiva de ${prioritizedProperties.length} marcadores...`);

      // Limpiar marcadores existentes
      markers.forEach(marker => {
        marker.setMap(null);
      });
      setMarkers([]);

      const newMarkers: google.maps.Marker[] = [];
      const BATCH_SIZE = 10;

      for (let i = 0; i < prioritizedProperties.length; i += BATCH_SIZE) {
        const batch = prioritizedProperties.slice(i, i + BATCH_SIZE);
        
        console.log(`🔄 Cargando lote ${Math.floor(i / BATCH_SIZE) + 1}/${Math.ceil(prioritizedProperties.length / BATCH_SIZE)} (${batch.length} marcadores)`);

        await new Promise(resolve => {
          setTimeout(async () => {
            for (const property of batch) {
              const position = getCoordinatesForSector(property);
              
              try {
                // Crear marcador básico con ícono personalizado
                const marker = new google.maps.Marker({
                  position,
                  map,
                  title: `${property.tipo_inmueble} en ${property.sector}`,
                  icon: createCustomIcon(property),
                  animation: google.maps.Animation.DROP
                });

                // Crear info window mejorado
                const infoWindow = new google.maps.InfoWindow({
                  content: `
                    <div style="max-width: 320px; padding: 12px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
                      <div style="display: flex; align-items: center; margin-bottom: 10px;">
                        <span style="font-size: 24px; margin-right: 10px;">
                          ${property.tipo_inmueble === 'Apto' ? '🏢' : 
                            property.tipo_inmueble === 'Casa' ? '🏠' : 
                            property.tipo_inmueble === 'Local' ? '🏪' : 
                            property.tipo_inmueble === 'Oficina' ? '🏢' : 
                            property.tipo_inmueble === 'Consultorio' ? '🏥' : '🏭'}
                        </span>
                        <h3 style="margin: 0; font-size: 18px; font-weight: 600; color: #1f2937;">
                          ${property.tipo_inmueble} en ${property.sector}
                        </h3>
                      </div>
                      
                      <div style="background: linear-gradient(135deg, #10b981, #059669); color: white; padding: 10px; border-radius: 8px; margin-bottom: 10px; text-align: center;">
                        <p style="margin: 0; font-weight: 700; font-size: 18px;">
                          ${formatPrice(property)}
                        </p>
                      </div>
                      
                      <div style="font-size: 14px; color: #4b5563; margin-bottom: 12px; line-height: 1.5;">
                        <p style="margin: 4px 0; display: flex; align-items: center;">
                          <span style="margin-right: 8px;">📍</span> 
                          <strong>${property.direccion || `${property.sector}, ${property.ciudad}`}</strong>
                        </p>
                        <p style="margin: 4px 0; display: flex; align-items: center;">
                          <span style="margin-right: 8px;">📐</span> 
                          <strong>${property.area_total || property.area_privada || 'No especificada'} m²</strong>
                        </p>
                        ${property.habitaciones ? `
                          <p style="margin: 4px 0; display: flex; align-items: center;">
                            <span style="margin-right: 8px;">🛏️</span> 
                            ${property.habitaciones} habitaciones
                          </p>
                        ` : ''}
                        ${property.banos ? `
                          <p style="margin: 4px 0; display: flex; align-items: center;">
                            <span style="margin-right: 8px;">🚿</span> 
                            ${property.banos} baños
                          </p>
                        ` : ''}
                        ${property.parqueaderos ? `
                          <p style="margin: 4px 0; display: flex; align-items: center;">
                            <span style="margin-right: 8px;">🚗</span> 
                            ${property.parqueaderos} parqueaderos
                          </p>
                        ` : ''}
                      </div>
                      
                      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                        <span style="background: ${property.estado === 'Disponible' ? '#10b981' : '#ef4444'}; 
                                   color: white; padding: 6px 12px; border-radius: 16px; font-size: 12px; font-weight: 600;">
                          ${property.estado}
                        </span>
                        <button onclick="window.selectProperty('${property.id}')" 
                                style="background: linear-gradient(135deg, #3b82f6, #2563eb); color: white; border: none; 
                                       padding: 8px 16px; border-radius: 8px; cursor: pointer; font-size: 13px; 
                                       font-weight: 600; transition: all 0.2s ease; box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);"
                                onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 4px 8px rgba(59, 130, 246, 0.4)'"
                                onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 4px rgba(59, 130, 246, 0.3)'">
                          Ver Detalles
                        </button>
                      </div>
                    </div>
                  `
                });

                // Evento click en marcador
                marker.addListener('click', () => {
                  // Cerrar otros info windows
                  newMarkers.forEach(m => {
                    if ((m as any).infoWindow) {
                      (m as any).infoWindow.close();
                    }
                  });
                  
                  infoWindow.open(map, marker);
                  (marker as any).infoWindow = infoWindow;
                });

                newMarkers.push(marker);
                
              } catch (markerError) {
                console.warn(`⚠️ Error creando marcador para propiedad ${property.id}:`, markerError);
              }
            }

            resolve(void 0);
          }, 50);
        });

        // Actualizar el estado con los marcadores cargados hasta ahora
        setMarkers([...newMarkers]);
        setVisibleProperties(prioritizedProperties.slice(0, newMarkers.length));
      }

      setLoadingMarkers(false);
      console.log(`✅ Carga completa: ${newMarkers.length} marcadores añadidos al mapa`);
    };

    addMarkersProgressively();

    // Función global para seleccionar propiedad desde el info window
    (window as any).selectProperty = (propertyId: string) => {
      const property = prioritizedProperties.find(p => p.id === propertyId);
      if (property && onPropertySelect) {
        onPropertySelect(property);
      }
    };

    return () => {
      (window as any).selectProperty = undefined;
    };
  }, [map, prioritizedProperties, onPropertySelect]);

  // Centrar en propiedad seleccionada
  useEffect(() => {
    if (!map || !selectedProperty) return;

    const position = getCoordinatesForSector(selectedProperty);
    map.setCenter(position);
    map.setZoom(15);

    // Encontrar y abrir el marcador correspondiente
    const marker = markers.find(m => {
      const markerPos = m.getPosition();
      return markerPos && 
             Math.abs(markerPos.lat() - position.lat) < 0.01 && 
             Math.abs(markerPos.lng() - position.lng) < 0.01;
    });

    if (marker) {
      google.maps.event.trigger(marker, 'click');
    }
  }, [selectedProperty, map, markers]);

  // Render con mejor manejo de estados
  if (loading) {
    return (
      <div 
        ref={mapRef}
        className="w-full h-full bg-gray-100 flex items-center justify-center rounded-lg"
        style={{ minHeight: '400px' }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Cargando mapa interactivo...</p>
          <p className="text-sm text-gray-500 mt-1">
            {isElementReady ? 'Conectando con Google Maps...' : 'Preparando contenedor...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        ref={mapRef}
        className="w-full h-full bg-red-50 border-2 border-red-200 rounded-lg flex items-center justify-center"
        style={{ minHeight: '400px' }}
      >
        <div className="text-center p-6">
          <div className="text-6xl mb-4">🗺️</div>
          <h3 className="text-lg font-semibold text-red-800 mb-2">Error al cargar el mapa</h3>
          <p className="text-red-600 mb-4 max-w-md">{error}</p>
          {retryCount < MAX_RETRIES && (
            <button
              onClick={retryMapLoad}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Reintentar ({MAX_RETRIES - retryCount} intentos restantes)
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      <div 
        ref={mapRef} 
        className="w-full h-full rounded-lg"
        style={{ minHeight: '400px' }}
      />
      
      {loadingMarkers && (
        <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg p-3 flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
          <span className="text-sm text-gray-600">
            Cargando propiedades... ({visibleProperties.length}/{prioritizedProperties.length})
          </span>
        </div>
      )}

      {/* Info sobre propiedades mostradas */}
      {!loadingMarkers && visibleProperties.length > 0 && (
        <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3">
          <p className="text-sm text-gray-600">
            📍 Mostrando <span className="font-semibold text-blue-600">{visibleProperties.length}</span> 
            {' '}de {properties.length} propiedades
          </p>
        </div>
      )}
    </div>
  );
};

export default MapComponent;